import { useMemo } from 'react';
import type { FeedMatch } from '../repositories/readerStory';
import type { UserEventContext } from '../repositories/storyRepository';
import type { BetaSearchSort } from '../utils/betaSearchSort';
import { DECISION_MODELS } from '../utils/decisionModels';
import { buildComplete, type FeedV2State, STOP_LABELS, summarizeWave, withFeedMembership } from '../utils/feedsV2';
import { sortV2Stories } from '../utils/feedsV2Sort';
import { BetaStoryList } from './BetaStoryList';

function modelLabel(slug: string): string {
  return DECISION_MODELS.find((m) => m.value === slug)?.label ?? slug;
}

/**
 * A feeds v2 build as it streams in: its ranked stories, progress while it
 * reads, and for admins the waves and every story's rank inputs. Used by a
 * saved feed (`/feeds/<id>`) and an unsaved search (`/search`).
 */
export function FeedV2Results({ state, sort = null, resetKey, eventContext, isAdmin, onRetry, savedFeed }: {
  state: FeedV2State;
  /** The reader's order for the story list; the admin details keep rank order. */
  sort?: BetaSearchSort;
  resetKey: string;
  eventContext: UserEventContext;
  /** Admin mode: show the build's details. */
  isAdmin: boolean;
  onRetry: () => void;
  /** The saved feed these stories are (absent for a search): each story is tagged with it, for its hide menu. */
  savedFeed?: FeedMatch;
}) {
  const { feed, waves, stories, ungraded, provisional, stop, done, error } = state;
  const read = waves.length ? waves[waves.length - 1].end : 0;
  const costUsd = waves.reduce((sum, wave) => sum + summarizeWave(wave).costUsd, 0);
  const sorted = useMemo(() => sortV2Stories(stories, sort), [stories, sort]);
  const shown = useMemo(
    () => savedFeed ? withFeedMembership(sorted, savedFeed) : sorted,
    [sorted, savedFeed?.id, savedFeed?.name],
  );
  // Finished without an error event, but the judge or grader failed (feedsV2.buildComplete): not an empty feed.
  const incomplete = done && !error && !buildComplete(state);
  return <>
    {error && (
      <div className="beta-error" role="alert">
        {error} <button type="button" onClick={onRetry}>Retry</button>
      </div>
    )}
    {incomplete && (
      <div className="beta-error" role="alert">
        Couldn’t finish ranking these stories: the judge or grader failed. <button type="button" onClick={onRetry}>Retry</button>
      </div>
    )}
    <div className="beta-feed-container">
      <BetaStoryList eventContext={eventContext} articles={shown} resetKey={resetKey} isAdmin={isAdmin} settled={done} />
    </div>
    {!done && (
      <p className="beta-message" role="status">
        <span className="spinner" style={{ marginRight: 8 }} />
        {stories.length === 0 ? 'Finding stories…' : provisional ? 'Checking for new stories…' : 'Finding more stories…'}
        {isAdmin && feed && ` Wave ${waves.length + 1} (${read.toLocaleString()} of ${feed.positions.toLocaleString()} read).`}
      </p>
    )}
    {done && !error && !incomplete && stories.length === 0 && <p className="beta-message beta-message-centered">No stories.</p>}
    {isAdmin && feed && (
      <details className="fts-details">
        <summary>
          Details · read {read.toLocaleString()} of {feed.positions.toLocaleString()} · {waves.length} waves
          {stop && ` · stopped: ${STOP_LABELS[stop]}`} · ${costUsd.toFixed(4)} judging
        </summary>
        <div className="fts-summary">
          <div>
            Relevance: {modelLabel(feed.model)}, one-pass prompt {feed.prompt_id.slice(0, 8)}. Rank grade:{' '}
            {modelLabel(feed.grade_model)}, prompt {feed.grade_prompt_id.slice(0, 8)}, time decay exponent{' '}
            {feed.time_decay_exponent}. Cosine queue (no BM25), {feed.window_hours}h, capped at{' '}
            {feed.max_positions.toLocaleString()}. Cached at start: {feed.cached_positions.toLocaleString()}{' '}
            verdicts, {feed.warm_waves} warm {feed.warm_waves === 1 ? 'wave' : 'waves'}.
            {feed.entity_match &&
              ` Entity wave: ${feed.entity_match.entity}, ${feed.entity_match.matches.toLocaleString()} queued articles` +
                ` naming it (${feed.entity_match.strong.toLocaleString()} in the title or first).`}
          </div>
          <details>
            <summary>Prompt</summary>
            <pre className="fts-rule">{feed.description}</pre>
          </details>
        </div>
        <table className="fts-table">
          <thead>
            <tr><th>Wave</th><th>Positions</th><th>Stories passing</th><th>Articles</th><th>Stop</th></tr>
          </thead>
          <tbody>
            {waves.map((wave) => {
              const summary = summarizeWave(wave);
              return (
                <tr key={wave.index}>
                  <td>{wave.index + 1}</td>
                  <td>{wave.start}–{wave.end}</td>
                  <td>{wave.passing} of {wave.stories}{wave.stories > 0 && ` (${Math.round((100 * wave.passing) / wave.stories)}%)`} · {wave.new} new</td>
                  <td className="fts-small">
                    {summary.relevant} relevant · {summary.cached} cached{summary.errors > 0 && ` · ${summary.errors} errors`}
                  </td>
                  <td>{wave.stop ? STOP_LABELS[wave.stop] : ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <table className="fts-table">
          <thead>
            <tr><th>Story</th><th>Rank score</th><th>Grade</th><th>Relevance</th><th>Age</th></tr>
          </thead>
          <tbody>
            {stories.map((story) => (
              <tr key={story.id}>
                <td>
                  <a href={story.url ?? undefined} target="_blank" rel="noreferrer">{story.title || story.id}</a>
                  <div className="fts-small fts-muted">{story.canonical_domain}</div>
                </td>
                <td><strong>{story.rank_score.toFixed(3)}</strong></td>
                <td>{story.rank_grade == null ? <span className="fts-muted">grading…</span>
                  : <>{story.rank_grade.toFixed(2)} <span className="fts-muted fts-small">at {story.rank_grade_confidence?.toFixed(2)}</span></>}</td>
                <td>{story.rank_relevance.toFixed(2)}</td>
                <td>{story.rank_age_hours.toFixed(1)}h</td>
              </tr>
            ))}
          </tbody>
        </table>
        {ungraded.length > 0 && (
          <details>
            <summary>{ungraded.length} relevant stories without a grade (not shown)</summary>
            <ul className="fts-small">
              {ungraded.map((story) => <li key={story.id}>{story.title || story.id}: {story.reason ?? 'not graded'}</li>)}
            </ul>
          </details>
        )}
      </details>
    )}
  </>;
}
